/**
 * cloakWriteGuard — Maker safety net for cross-role writes.
 *
 * Why this exists
 * ───────────────
 * The MAKER (`system_admin`) can cloak into any role to preview the app
 * as that role. v156 made the cloak state visually unmistakable, but it's
 * still possible to cloak as e.g. CFO and accidentally approve a real
 * invoice — those writes go through with the cloaked role's identity in
 * the audit log. This module gates write requests behind a confirm modal
 * whenever a cloak is active.
 *
 * Two safety tiers (v157 — option D from the design discussion)
 * ────────────────────────────────────────────────────────────
 * RISKY writes (approvals, deletes, role/permission mutations, billing,
 *               admin endpoints) → ALWAYS confirm. No "don't ask again"
 *               override. The Maker must intentionally consent each time.
 *
 * BENIGN writes (everything else) → confirm once per cloak session, with
 *               a "Don't ask again this cloak session" checkbox. Opt-in
 *               clears the moment the Maker exits or changes cloak.
 *
 * Wire-up
 * ───────
 *   • Axios request interceptor in `lib/api.js` calls `maybeConfirmCloakedWrite`
 *     on every POST/PATCH/PUT/DELETE. If it returns false → cancel the
 *     request with an Axios cancel.
 *   • `<CloakWriteConfirmModal />` mounts at AppRouter level, subscribes
 *     via `subscribePendingConfirm`, and resolves each pending Promise.
 *   • `AuthContext.setImpersonatedRole` calls `resetCloakOptIn()` on
 *     every cloak change so the session opt-in flag doesn't leak between
 *     cloaks.
 */

const OPT_IN_KEY = 've_cloak_write_optin';

// ──────────────────────────────────────────────────────────────
// Risky-endpoint matchers. Order: most-specific first.
// Each pattern is checked against the LOWERCASED url-path (NOT including
// the /api prefix — Axios interceptor strips the baseURL).
// ──────────────────────────────────────────────────────────────
const RISKY_PATTERNS = [
  // Identity / authority mutations
  /\/users\/role\b/i,
  /\/users\/[^/]+\/role\b/i,
  /\/users\/[^/]+\/deactivate\b/i,
  /\/roles(?:\/|$)/i,
  /\/permissions(?:\/|$)/i,

  // Approval workflows — any role-gated step
  /\/approvals?(?:\/|$)/i,
  /\/workflow[s]?\/.*\/(approve|reject|step)/i,
  /\/invoices?\/[^/]+\/(approve|reject|sign)/i,
  /\/agreements?\/[^/]+\/(sign|approve|reject)/i,
  /\/vetting\/.*\/(approve|reject)/i,

  // Money movement
  /\/billing\/(payouts|refund|charge|invoice)/i,
  /\/payments?\/(refund|capture|payout)/i,
  /\/stripe\/(refund|payout)/i,
  /\/subscriptions?\/.*\/(approve|reject|cancel)/i,

  // Platform-level admin
  /\/admin\/(.*\/)?(reset|purge|delete|seed|migrate|drain)/i,
  /\/system\/(.*\/)?(reset|purge|delete)/i,

  // Tenant + branding mutations affecting all users in that tenant
  /\/tenants?\/[^/]+\/(activate|deactivate|delete|purge|migrate)/i,
  /\/branding\/(publish|reset|delete)/i,
];

const WRITE_METHODS = new Set(['post', 'patch', 'put', 'delete']);

// Endpoints that must NEVER be gated (they ARE the safety controls):
const NEVER_GATE = [
  /\/auth\/impersonate\b/i,   // exit-cloak path
  /\/auth\/logout\b/i,        // emergency exit
];

export function isWriteRequest(method) {
  return !!method && WRITE_METHODS.has(String(method).toLowerCase());
}

export function isRiskyWrite(method, url) {
  if (!isWriteRequest(method)) return false;
  const u = String(url || '');
  if (NEVER_GATE.some((re) => re.test(u))) return false;
  // DELETE is structurally risky no matter what.
  if (String(method).toLowerCase() === 'delete') return true;
  return RISKY_PATTERNS.some((re) => re.test(u));
}

export function shouldEverGate(method, url) {
  if (!isWriteRequest(method)) return false;
  return !NEVER_GATE.some((re) => re.test(String(url || '')));
}

// ──────────────────────────────────────────────────────────────
// Session opt-in (BENIGN writes only — risky writes never honour this)
// ──────────────────────────────────────────────────────────────
export function hasCloakOptIn() {
  try { return sessionStorage.getItem(OPT_IN_KEY) === '1'; }
  catch { return false; }
}

export function setCloakOptIn(v) {
  try {
    if (v) sessionStorage.setItem(OPT_IN_KEY, '1');
    else sessionStorage.removeItem(OPT_IN_KEY);
  } catch { /* noop — private mode etc. */ }
}

export function resetCloakOptIn() {
  setCloakOptIn(false);
}

// ──────────────────────────────────────────────────────────────
// Pub/sub for the confirm modal
// ──────────────────────────────────────────────────────────────
let listener = null;
let pending = []; // queue when no listener mounted yet (race during boot)

export function subscribePendingConfirm(cb) {
  listener = cb;
  // Flush any pre-mount queue
  if (pending.length && listener) {
    const drain = pending.slice();
    pending = [];
    drain.forEach(listener);
  }
  return () => { if (listener === cb) listener = null; };
}

function enqueueConfirm(req) {
  return new Promise((resolve) => {
    const payload = { ...req, resolve };
    if (listener) listener(payload);
    else pending.push(payload);
  });
}

/**
 * Called by the Axios interceptor on every write while cloaked.
 * Returns Promise<boolean> — true means "send the request",
 * false means "cancel the request".
 */
export async function maybeConfirmCloakedWrite({ method, url, cloakedAs }) {
  if (!shouldEverGate(method, url)) return true;
  const risky = isRiskyWrite(method, url);
  // Benign writes can be silently allowed if opt-in is set.
  if (!risky && hasCloakOptIn()) return true;
  return enqueueConfirm({
    method: String(method).toUpperCase(),
    url,
    cloakedAs,
    risky,
  });
}
